import { HomeNews } from "./Engagement";
import { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { loadSampleData, removeSampleData, resetSampleData, sampleDataLoaded } from "../data";
import { NeedsAttention } from "../components/NeedsAttention";
import { Reminders } from "../components/Reminders";
import type { Workspace } from "../types";

export function Home({ ws, onOpen, onChanged }: { ws: Workspace; onOpen: (id: string) => void; onChanged: () => void }) {
  const { t, pick } = useI18n();
  const companyId = ws.membership.company_id;
  const name = ws.membership.display_name?.split(" ")[0] ?? "";
  const on = ws.catalog.filter((m) => ws.enabled.has(m.key));
  const isAdmin = ws.permissions.has("company.manage");
  const [sample, setSample] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  useEffect(() => {
    if (isAdmin) void sampleDataLoaded(companyId).then(setSample).catch(() => setSample(null));
  }, [companyId, isAdmin]);

  async function load() {
    setBusy(true);
    setMessage(null);
    try {
      const n = await loadSampleData(companyId);
      setSample(true);
      setMessage({ tone: "success", text: `${n} ${t("sampleLoaded")}` });
      onChanged();
    } catch (err) {
      setMessage({ tone: "danger", text: err instanceof Error ? err.message : t("errorGeneric") });
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setBusy(true);
    setMessage(null);
    try {
      const n = await resetSampleData(companyId);
      setMessage({ tone: "success", text: `${n} ${t("sampleLoaded")}` });
      onChanged();
    } catch (err) {
      setMessage({ tone: "danger", text: err instanceof Error ? err.message : t("errorGeneric") });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setMessage(null);
    try {
      const n = await removeSampleData(companyId);
      setSample(false);
      setConfirmRemove(false);
      setMessage({ tone: "success", text: `${n} ${t("sampleRemoved")}` });
      onChanged();
    } catch (err) {
      setMessage({ tone: "danger", text: err instanceof Error ? err.message : t("errorGeneric") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <h1 className="display">
        {t("greeting")}
        {name ? `, ${name}` : ""}
      </h1>
      <p className="muted">{pick(ws.membership.company.name, ws.membership.company.name_ar)}</p>
      <div className="stats">
        <div className="stat">
          <span className="stat-value">{on.length}</span>
          <span className="stat-label">{t("modulesOn")}</span>
        </div>
        <div className="stat">
          <span className="stat-value small">{(ws.membership.role ? pick(ws.membership.role.name_en, ws.membership.role.name_ar) : "")}</span>
          <span className="stat-label">{t("yourRole")}</span>
        </div>
      </div>

      {ws.enabled.has("engagement") && <HomeNews ws={ws} onMore={() => onOpen("engagement")} />}
      <Reminders ws={ws} onGo={onOpen} />
      <NeedsAttention ws={ws} onOpen={onOpen} />

      {isAdmin && sample !== null && (
        <section className={`card${sample ? "" : " highlight"}`}>
          <h2>{sample ? t("sampleOnTitle") : t("sampleTitle")}</h2>
          <p className="muted">{sample ? t("sampleOnBody") : t("sampleBody")}</p>
          {message && <p className={`alert ${message.tone === "success" ? "info" : "danger"}`} role="status">{message.text}</p>}
          <div className="row">
            {!sample && (
              <button type="button" className="btn spark" onClick={load} disabled={busy}>
                {busy ? t("loading") : t("sampleLoad")}
              </button>
            )}
            {sample && !confirmRemove && (
              <>
                <button type="button" className="btn primary" onClick={() => onOpen("employees")}>{t("navEmployees")}</button>
                <button type="button" className="btn secondary" onClick={reset} disabled={busy}>{busy ? t("loading") : t("sampleReset")}</button>
                <button type="button" className="btn secondary" onClick={() => setConfirmRemove(true)} disabled={busy}>{t("sampleRemove")}</button>
              </>
            )}
            {sample && confirmRemove && (
              <>
                <span className="muted">{t("sampleRemoveConfirm")}</span>
                <button type="button" className="btn secondary" onClick={() => setConfirmRemove(false)} disabled={busy}>{t("cancel")}</button>
                <button type="button" className="btn danger" onClick={remove} disabled={busy}>{t("sampleRemove")}</button>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
